<template>
  <div class="flex">
    <div
      class="fixed top-18 z-40 w-88 flex-shrink-0 border-r border-slate-400 bg-pure-white"
    >
      <div class="py-6 px-6 border-b border-slate-400 flex flex-col gap-3">
        <div>
          <BlTextTitle3 text="My Sessions" />
          <div class="mt-1">
            <BlTextBody2 text="These are the sessions assigned to you." />
          </div>
        </div>
        <div
          class="py-1 px-1 flex bg-slate-400"
          :style="{ borderRadius: '8px', height: '2.375rem' }"
        >
          <div
            v-for="opt in previewTypeOptions"
            :key="opt.value"
            class="h-full w-full flex items-center justify-center gap-1 rounded cursor-pointer transition-all duration-300"
            :class="[
              previewTypeInput === opt.value ? 'bg-pure-white' : 'bg-slate-400'
            ]"
            @click="onChangePreviewType(opt.value)"
          >
            <BlIcon :icon="opt.icon" color="slate-6" class="w-4 h-4" />
            <BlTextSubtitle3 :text="opt.label" />
          </div>
        </div>
      </div>

      <div
        v-if="pageLoading"
        class="w-full grid place-items-center"
        :style="{ height: 'calc(100vh - 15.5rem)' }"
      ></div>
      <div
        v-else
        class="overflow-y-auto"
        :style="{ height: 'calc(100vh - 15.5rem)' }"
      >
        <div v-if="previewTypeInput === 'calendar'" id="calendar-section">
          <div class="border-b border-slate-400 flex justify-center relative">
            <div
              class="absolute z-10 flex items-center gap-1 bg-pure-white"
              :style="{ right: '10px', top: '8px', height: '32px' }"
            >
              <BlInputButton
                type="outlined"
                dense
                label="Today"
                class="px-2"
                @clicked="onDayClick({ date: new Date() })"
              />
              <BlInputButton
                type="outlined"
                dense
                icon="chevron-left"
                class="border-none"
                @clicked="onMoveCalendar(-1)"
              />
              <BlInputButton
                type="outlined"
                dense
                icon="chevron-right"
                class="border-none"
                @clicked="onMoveCalendar(1)"
              />
            </div>
            <v-calendar
              ref="calendar"
              is-expanded
              hide-header
              title-position="left"
              :attributes="attrs"
              class="border-none custom-calendar"
              @dayclick="onDayClick"
            />
          </div>
        </div>

        <div
          v-if="previewTypeInput === 'list'"
          id="list-section"
          class="py-5 px-6"
        >
          <div class="flex items-center justify-between mb-3">
            <BlTextSubtitle3 text="Filters" class="text-slate-600" />
            <BlInputButton
              class="px-0"
              type="plain"
              dense
              label="Clear all"
              @clicked="onClearAll"
            />
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <BlIcon icon="user-square" color="slate-6" class="h-6 w-6" />
              <BlSingleFilter
                class="w-68"
                label="Client"
                placeholder="By client"
                v-model="filterClient"
                search
                searchPlaceholder="Search by name"
                :fetchProperty="{
                  url: `/api/v1/clients?sort=alphabetical&status=active&query=`,
                  dataKey: 'clients',
                  countKey: 'total_clients',
                  optionKeys: { value: 'id', label: 'name' }
                }"
                @change="onFilterClient"
              />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="calendar-blank" color="slate-6" class="h-6 w-6" />
              <BlSingleFilterDate
                class="w-68"
                v-model="filterDate"
                @change="onFilterDate"
              />
            </div>
            <div v-if="enableBranch" class="flex items-center gap-2">
              <BlIcon icon="location" color="slate-6" class="h-6 w-6" />
              <BlSingleFilter
                class="w-68"
                label="Branch"
                placeholder="By branch"
                v-model="filterBranch"
                search
                searchPlaceholder="Search by name"
                :fetchProperty="{
                  url: `/api/v1/branches?sort=name_asc&query=`,
                  dataKey: 'branches',
                  countKey: 'total_count',
                  optionKeys: {
                    value: 'id',
                    label: 'name',
                    color: 'color',
                    backgroundColor: 'background_color'
                  }
                }"
                @change="onFilterBranch"
              />
            </div>
            <div v-if="enableAppointment" class="flex items-center gap-2">
              <BlIcon icon="door" color="slate-6" class="h-6 w-6" />
              <BlSingleFilter
                class="w-68"
                label="Room"
                placeholder="By room"
                v-model="filterRoom"
                search
                searchPlaceholder="Search by name"
                :fetchProperty="{
                  url: `/api/v1/rooms?query=`,
                  dataKey: 'rooms',
                  countKey: 'total_count',
                  optionKeys: { value: 'id', label: 'name' }
                }"
                @change="onFilterRoom"
              />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="loading" color="slate-6" class="h-6 w-6" />
              <BlSingleFilter
                class="w-68"
                label="Status"
                placeholder="By status"
                v-model="filterStatus"
                :options="filterStatusOptions"
                @change="onFilterStatus"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="previewTypeInput === 'calendar'" class="pl-88">
      <div
        class="h-12 px-6 sticky top-18 z-30 bg-slate-300 border-b border-slate-400 flex items-center justify-between gap-4"
        :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
      >
        <div class="sticky top-18 left-94">
          <BlTextBody2 :text="masterDate" />
        </div>
        <div class="sticky top-18 right-6 flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="timeslotView = $event"
            />
          </div>
          <BlInputButton
            type="outlined"
            dense
            label="Jump to"
            @clicked="jumpToOpened = true"
          />
          <BlInputButton
            type="outlined"
            dense
            label="Today"
            @clicked="onDayClick({ date: new Date() })"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-left"
            @clicked="onNextClick(-1)"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-right"
            @clicked="onNextClick(1)"
          />
        </div>
      </div>
      <Timeslots
        :isCompact="timeslotView === 'compact'"
        :selectedDate="date"
        :headers="timeslotHeaders"
        :loading="previewLoading"
      />
    </div>

    <div v-if="previewTypeInput === 'list'" class="pl-88 bg-slate-100">
      <div
        class="h-16 px-6 sticky top-18 z-20 bg-slate-300 border-b border-slate-400 flex items-center justify-between gap-4"
        :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
      >
        <div class="sticky top-18 left-94">
          <BlInputSearch
            class="w-80"
            :input="searchInput"
            @changed="onInputSearch"
          />
        </div>
      </div>
      <div>
        <div v-if="previewLoading" class="w-full h-92 grid place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!mySessions || !mySessions.length"
          class="h-92 grid flex-col place-content-center text-center"
        >
          <BlTextBody2
            text="No appointments scheduled"
            class="px-4 font-semibold"
          />
          <BlTextBody2
            text="You don't have any appointments assigned to you right now. Enjoy the downtime!"
            class="px-4"
          />
        </div>
        <MySessionsTable v-else />
      </div>

      <div v-if="totalMySessions > 25" class="flex">
        <BlInputPagination
          class="sticky left-94 my-10"
          :style="{ minWidth: 'calc(100vw - 22rem - 3rem - 0.5rem)' }"
          :page="page"
          :perPage="perPage"
          :total="totalMySessions"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
    </div>

    <JumpToDateModal
      v-if="jumpToOpened"
      :currentDate="date"
      dimension="daily"
      @close-modal="jumpToOpened = false"
      @submit="onDayClick({ date: $event })"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import debounce from "lodash/debounce";

import JumpToDateModal from "../availabilities/timeslot/JumpToDateModal.vue";
import Timeslots from "./timeslot/Timeslots.vue";
import MySessionsTable from "./table/Table.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputCheck from "../BlInputCheck.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlIcon from "../BlIcon.vue";
import BlInputPagination from "../BlInputPagination.vue";
import BlSingleFilter from "../BlSingleFilter.vue";
import BlSingleFilterDate from "../BlSingleFilterDate.vue";

export default {
  name: "MySessions",
  components: {
    JumpToDateModal,
    Timeslots,
    MySessionsTable,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputSearch,
    BlInputButton,
    BlInputCheck,
    BlSelectBox,
    BlIcon,
    BlInputPagination,
    BlSingleFilter,
    BlSingleFilterDate
  },
  data() {
    return {
      timeslotView: "compact", // detailed | compact
      timeslotViewOptions: [
        {
          value: "detailed",
          label: "Detailed view",
          class: "font-bold text-light-purple"
        },
        {
          value: "compact",
          label: "Compact view",
          class: "font-bold text-light-purple"
        }
      ],
      pageLoading: true,
      previewLoading: false,
      exportLoading: false,
      date: new Date(),
      previewTypeInput: "calendar", // calendar | list
      previewTypeOptions: [
        { value: "calendar", label: "Calendar", icon: "calendar-blank" },
        { value: "list", label: "List", icon: "list" }
      ],
      jumpToOpened: false,
      //
      filterClient: [],
      filterDate: { label: "", start: "", end: "" },
      filterBranch: [],
      filterRoom: [],
      filterStatus: [],
      filterStatusOptions: [
        { value: "scheduled", chip: true },
        { value: "in_progress", chip: true },
        { value: "completed", chip: true },
        { value: "cancelled", chip: true }
      ],
      //
      newAppointmentOpened: false,
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search client",
        model: ""
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "branches",
      "rooms",
      "myCalendar",
      "mySessions",
      "totalMySessions"
    ]),
    enableAppointment() {
      return this.currentCenter.enable_appointment;
    },
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    getCalendarParams() {
      const d = moment(this.date)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      let params = `?view=calendar&date=${d}`;
      return params;
    },
    getListParams() {
      let params = ``;
      if (this.searchInput.model) {
        params += `&query=${this.searchInput.model}`;
      }
      if (this.filterClient.length) {
        params += `&client_id=${this.filterClient.join(",")}`;
      }
      if (this.filterDate.label) {
        if (this.filterDate.start) {
          params += `&start_date=${this.filterDate.start}`;
        }
        if (this.filterDate.end) params += `&end_date=${this.filterDate.end}`;
      }
      if (this.filterBranch.length) {
        params += `&branch_id=${this.filterBranch.join(",")}`;
      }
      if (this.filterRoom.length) {
        params += `&room_id=${this.filterRoom.join(",")}`;
      }
      if (this.filterStatus.length) {
        params += `&status=${this.filterStatus.join(",")}`;
      }
      return params;
    },
    attrs() {
      const solid = "bg-light-purple border-2 border-light-purple";
      const light = "bg-light-purple-200 border-2 border-light-purple-200";

      const today = {
        key: "today",
        highlight: { class: solid, fillMode: "solid" },
        dates: new Date()
      };
      const selected = {
        key: "selected",
        highlight: { class: light, fillMode: "light" },
        dates: this.date
      };

      const isSameDay =
        moment().format("YYYY-MM-DD") ===
        moment(this.date).format("YYYY-MM-DD");
      if (isSameDay) return [today];

      return [today, selected];
    },
    masterDate() {
      const start = moment(this.date)
        .startOf("isoWeek")
        .format("ddd, DD MMM YYYY");
      const end = moment(this.date)
        .endOf("isoWeek")
        .format("ddd, DD MMM YYYY");
      return `${start} — ${end}`;
    },
    timeslotHeaders() {
      const startDate = moment(this.date)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      const endDate = moment(this.date)
        .endOf("isoWeek")
        .format("YYYY-MM-DD");

      let current = moment(startDate);

      const dates = [];

      while (current.isSameOrBefore(endDate)) {
        const isSame =
          current.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
        dates.push({
          highlight: isSame,
          value: current.format("YYYY-MM-DD"),
          label: current.format("ddd DD")
        });
        current.add(1, "days");
      }

      return dates;
    }
  },
  async mounted() {
    const app = document.getElementById("app");
    if (app) {
      app.classList.add("bg-slate-100");
      app.classList.remove("bg-slate-200");
    }

    const isValidDate = d => {
      return d instanceof Date && !isNaN(d);
    };

    const searchParams = this.$route.query;
    if (searchParams["preview_type"]) {
      const found = this.previewTypeOptions.find(
        i => i.value === searchParams["preview_type"]
      );
      if (found) this.previewTypeInput = found.value;
    }
    if (searchParams["date"]) {
      const d = new Date(`${searchParams["date"]}T00:00:00`);
      if (isValidDate(d)) this.date = d;
    }
    //
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }
    if (searchParams["client_id"]) {
      this.filterClient = searchParams["client_id"]
        .split(",")
        .map(i => Number(i));
    }
    if (searchParams["start_date"]) {
      const d = new Date(`${searchParams["start_date"]}T00:00:00`);
      if (isValidDate(d)) {
        this.filterDate.label = "Custom";
        this.filterDate.start = searchParams["start_date"];
      }
    }
    if (searchParams["end_date"]) {
      const d = new Date(`${searchParams["end_date"]}T00:00:00`);
      if (isValidDate(d)) {
        this.filterDate.label = "Custom";
        this.filterDate.end = searchParams["end_date"];
      }
    }
    if (searchParams["branch_id"]) {
      this.filterBranch = searchParams["branch_id"]
        .split(",")
        .map(i => Number(i));
    }
    if (searchParams["room_id"]) {
      this.filterRoom = searchParams["room_id"]
        .split(",")
        .map(i => Number(i));
    }
    if (searchParams["status"]) {
      this.filterStatus = searchParams["status"].split(",");
    }

    this.pageLoading = true;
    this.previewLoading = true;

    const type = searchParams["preview_type"] === "list" ? "list" : "calendar";
    this.onChangePreviewType(type);
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) {
      app.scroll({ top: 0, left: 0 });
      app.classList.remove("bg-slate-100");
      app.classList.add("bg-slate-200");
    }
  },
  methods: {
    ...mapActions(["getClients", "getBranches", "getMySessions"]),
    fetchCalendar() {
      if (this.$route.path !== "/my-sessions") return;

      this.previewLoading = true;
      let params = this.getCalendarParams;
      this.getMySessions({ params }).then(async () => {
        this.previewLoading = false;
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/my-sessions${this.getCalendarParams}&preview_type=calendar&type=weekly`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    fetchList() {
      if (this.$route.path !== "/my-sessions") return;
      this.previewLoading = true;
      const params = `?page=${this.page}&per_page=${this.perPage}${this.getListParams}`;
      this.getMySessions({ params }).then(async () => {
        this.previewLoading = false;
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/my-sessions${params}&preview_type=list`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    async onChangePreviewType(type) {
      this.previewTypeInput = type;
      this.previewLoading = true;
      if (type === "calendar") {
        this.fetchCalendar();
      }
      if (type === "list") {
        this.fetchList();
      }
    },
    async onSyncCalendar(d) {
      const month = moment(d).format("M");
      const year = moment(d).format("YYYY");
      const calendar = this.$refs.calendar;
      if (calendar) await calendar.move({ month, year });
    },
    async onMoveCalendar(month) {
      const calendar = this.$refs.calendar;
      if (calendar) await calendar.move(month);
    },
    onDayClick(day) {
      const d = day.date;
      this.date = d;
      this.onSyncCalendar(d);
      this.fetchCalendar();
    },
    onNextClick(num) {
      const change = 7;
      const d = new Date(this.date);
      if (num === 1) {
        this.onDayClick({ date: d.setDate(d.getDate() + change) });
      } else {
        this.onDayClick({ date: d.setDate(d.getDate() - change) });
      }
    },
    //
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchList();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchList();
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchList();
    }, 1000),
    onClearAll() {
      this.filterClient = [];
      this.filterDate = { label: "", start: "", end: "" };
      this.filterBranch = [];
      this.filterRoom = [];
      this.filterStatus = [];
      this.fetchList();
    },
    onlyUnique(value, index, array) {
      return array.map(i => i.value).indexOf(value.value) === index;
    },
    isDiff(arr, arr2) {
      if (arr.length !== arr2.length) {
        return true;
      } else {
        let diff = false;
        arr.forEach((i, idx) => {
          if (i !== arr2[idx]) diff = true;
        });
        return diff;
      }
    },
    onFilterClient(val) {
      const isDiff = this.isDiff(this.filterClient, val);
      this.filterClient = val;
      if (isDiff) {
        this.page = 1;
        this.fetchList();
      }
    },
    onFilterDate(val) {
      let isDiff = false;
      if (val.start !== this.filterDate.start) isDiff = true;
      if (val.end !== this.filterDate.end) isDiff = true;
      this.filterDate = val;
      if (isDiff) {
        this.page = 1;
        this.fetchList();
      }
    },
    onFilterBranch(val) {
      const isDiff = this.isDiff(this.filterBranch, val);
      this.filterBranch = val;
      if (isDiff) {
        this.page = 1;
        this.fetchList();
      }
    },
    onFilterRoom(val) {
      const isDiff = this.isDiff(this.filterRoom, val);
      this.filterRoom = val;
      if (isDiff) {
        this.page = 1;
        this.fetchList();
      }
    },
    onFilterStatus(val) {
      const isDiff = this.isDiff(this.filterStatus, val);
      this.filterStatus = val;
      if (isDiff) {
        this.page = 1;
        this.fetchList();
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/mySessions/MySessions.vue